import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Button,
  Typography,
  Stack,
  InputAdornment,
  Divider,
  Link as MuiLink,
  IconButton,
  Alert,
} from '@mui/material';
import {
  Email as EmailIcon,
  Lock as LockIcon,
  ArrowBack as ArrowBackIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import authApi from '../api';
import type { OtpSendRequest, OtpVerifyRequest, LoginRequest } from '@/types/auth';
import { useAuth } from '@/hooks';
import { useLoading } from '@/hooks/useLoading';
import FullScreenLoading from '@/components/common/FullLoading';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import {
  OTP_VERIFICATION_TIMEOUT,
  OTP_RESEND_COOLDOWN,
  EMAIL_PATTERN,
  OTP_CODE_LENGTH,
  OTP_CODE_PATTERN,
} from '@/constants/auth';
import { DEFAULT_AUTHENTICATED_ROUTE } from '@/constants/routes';
import { isWebView, getWebViewAppName } from '@/utils/detectWebView';
import ParticleBackground from '@/components/common/ParticleBackground';

interface OtpLoginFormData {
  email: string;
  password: string;
}

interface OtpVerificationFormData {
  otp: string;
}

export default function LoginPage() {
  const { t } = useTranslation('auth');
  const [searchParams] = useSearchParams();
  const { setTokenAuth } = useAuth();
  const { loading, setLoading } = useLoading();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  // WebView detection - Google OAuth is blocked in WebViews
  const inWebView = isWebView();
  const webViewAppName = getWebViewAppName();
  const [urlCopied, setUrlCopied] = useState(false);

  // Copy URL handler for WebView users
  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setUrlCopied(true);
      toast.success(t('login.urlCopied'));
      setTimeout(() => setUrlCopied(false), 3000);
    } catch {
      toast.error(t('login.urlCopyFailed'));
    }
  };

  // OTP flow state
  const [otpStep, setOtpStep] = useState<'email' | 'verification'>('email');
  const [otpToken, setOtpToken] = useState<string>('');
  const [otpEmail, setOtpEmail] = useState<string>('');
  const [otpResendCountdown, setOtpResendCountdown] = useState<number>(0);
  const [verificationTimeRemaining, setVerificationTimeRemaining] = useState<number>(0);

  // Handle OAuth callback
  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      setLoading(true);
      try {
        setTokenAuth({
          access_token: token,
          refresh_token: '', // OAuth does not provide refresh_token, use empty string
        });
        toast.success(t('login.oauthLoginSuccess'));

        // Check for returnUrl from guest mode
        const returnUrl = sessionStorage.getItem('returnUrl');
        if (returnUrl) {
          sessionStorage.removeItem('returnUrl');
          navigate(returnUrl);
        } else {
          navigate(DEFAULT_AUTHENTICATED_ROUTE);
        }
      } catch (error) {
        console.error('OAuth callback handling failed:', error);
        toast.error(t('login.oauthLoginFailed'));
      } finally {
        setLoading(false);
      }
    }
  }, [searchParams, setTokenAuth, setLoading, navigate, t]);

  // Countdown effect (OTP resend)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (otpResendCountdown > 0) {
      timer = setTimeout(() => setOtpResendCountdown((prev) => prev - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [otpResendCountdown]);

  // Countdown effect (OTP expiration - 3 minutes)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;
    if (verificationTimeRemaining > 0) {
      timer = setTimeout(() => setVerificationTimeRemaining((prev) => prev - 1), 1000);
    } else if (verificationTimeRemaining === 0 && otpStep === 'verification' && otpEmail) {
      // OTP expired, return to email input step
      toast.error(t('login.otpExpired'));
      setOtpStep('email');
      setOtpToken('');
      setOtpEmail('');
      setOtpResendCountdown(0);
    }
    return () => clearTimeout(timer);
  }, [verificationTimeRemaining, otpStep, otpEmail, t]);

  // Send OTP form
  const {
    register: registerOtpEmail,
    handleSubmit: handleSubmitOtpEmail,
    formState: { errors: errorsOtpEmail },
    watch: watchOtpEmail,
  } = useForm<OtpLoginFormData>({
    mode: 'onBlur',
  });

  const emailValue = watchOtpEmail('email');
  const passwordValue = watchOtpEmail('password');

  // Password login Mutation (Step 1: Verify password and send OTP)
  const passwordLoginMutation = useMutation({
    mutationFn: (data: LoginRequest) => authApi.passwordLogin(data),
    onMutate: () => setLoading(true),
    onSuccess: (response) => {
      setOtpToken(response.otpToken);
      setOtpEmail(response.email || emailValue);
      setOtpStep('verification');
      setOtpResendCountdown(OTP_RESEND_COOLDOWN);
      setVerificationTimeRemaining(OTP_VERIFICATION_TIMEOUT);
      toast.success(t('login.otpSent', { minutes: OTP_VERIFICATION_TIMEOUT / 60 }));
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  // Resend OTP Mutation
  const sendOtpMutation = useMutation({
    mutationFn: (data: OtpSendRequest) => authApi.sendOtp(data),
    onMutate: () => setLoading(true),
    onSuccess: (response) => {
      setOtpToken(response.otpToken);
      setOtpResendCountdown(OTP_RESEND_COOLDOWN);
      setVerificationTimeRemaining(OTP_VERIFICATION_TIMEOUT);
      toast.success(t('login.newOtpSent', { minutes: OTP_VERIFICATION_TIMEOUT / 60 }));
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  // Verify OTP form
  const {
    register: registerOtpCode,
    handleSubmit: handleSubmitOtpCode,
    formState: { errors: errorsOtpCode },
    reset: resetOtpCode,
  } = useForm<OtpVerificationFormData>();

  // Verify OTP Mutation
  const verifyOtpMutation = useMutation({
    mutationFn: (data: OtpVerifyRequest) => authApi.verifyOtp(data),
    onMutate: () => setLoading(true),
    onSuccess: (response) => {
      setTokenAuth({
        access_token: response.access_token,
        refresh_token: response.refresh_token,
      });
      toast.success(t('login.loginSuccess'));
      setLoading(false);
      const returnUrl = sessionStorage.getItem('returnUrl');
      if (returnUrl) {
        sessionStorage.removeItem('returnUrl');
        navigate(returnUrl);
      } else {
        navigate(DEFAULT_AUTHENTICATED_ROUTE);
      }
    },
    onError: () => {
      resetOtpCode();
      setLoading(false);
    },
  });

  const onSubmitEmail = (data: OtpLoginFormData) => {
    passwordLoginMutation.mutate({ email: data.email, password: data.password });
  };

  const onSubmitOtp = (data: OtpVerificationFormData) => {
    if (!otpEmail || !otpToken) {
      toast.error(t('login.verification.otpAbnormal'));
      setOtpStep('email');
      return;
    }
    verifyOtpMutation.mutate({ email: otpEmail, otpToken, otp: data.otp });
  };

  const handleBackToEmail = () => {
    setOtpStep('email');
    setOtpToken('');
    setOtpEmail('');
    setOtpResendCountdown(0);
    setVerificationTimeRemaining(0);
    resetOtpCode();
  };

  const handleResendOtp = () => {
    sendOtpMutation.mutate({ email: otpEmail, otpToken });
  };

  const handleGoogleLogin = () => {
    authApi.loginWithGoogle();
  };

  // ── Shared input styles ──
  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      background: 'rgba(255,255,255,0.05)',
      color: 'rgba(255,255,255,0.9)',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
      '&.Mui-focused fieldset': { borderColor: '#00B8D4' },
    },
    '& .MuiInputBase-input::placeholder': {
      color: 'rgba(255,255,255,0.4)',
      opacity: 1,
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: 'rgba(255,255,255,0.4)',
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100vh',
        background: '#0a0a0a',
      }}
    >
      <FullScreenLoading open={loading} />

      {/* ── Left: Form Panel ──────────────────────── */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          px: { xs: 3, sm: 6 },
          py: 4,
          position: 'relative',
        }}
      >
        {/* Top bar: Home + controls */}
        <Box
          sx={{
            position: 'absolute',
            top: 20,
            left: 24,
            right: 24,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <MuiLink
            href="/"
            sx={{
              color: 'rgba(255,255,255,0.6)',
              textDecoration: 'none',
              fontSize: '0.95rem',
              '&:hover': { color: '#fff' },
              transition: 'color 0.2s',
            }}
          >
            ← {t('login.backToHome')}
          </MuiLink>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <LanguageSwitcher />
          </Box>
        </Box>

        {/* Form content */}
        <Box sx={{ width: '100%', maxWidth: 380 }}>
          {/* Logo + Title */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, mb: 1 }}>
            <Typography
              sx={{
                fontFamily:
                  'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
                fontWeight: 400,
                fontSize: '1.8rem',
                color: '#fff',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
              }}
            >
              {otpStep === 'email' ? t('login.welcomeBack') : t('login.verification.title')}
            </Typography>
            <Box
              component="img"
              src="/intellio-wordmark-dark.svg"
              alt="Intellio AI"
              sx={{ height: 40, mb: '-4px', ml: -1 }}
            />
          </Box>
          <Typography
            sx={{
              fontFamily:
                'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.9rem',
              fontWeight: 500,
              mb: 4,
            }}
          >
            {otpStep === 'email'
              ? t('login.subtitle')
              : t('login.verification.codeSentTo') + ' ' + otpEmail}
          </Typography>

          {/* ── Email/Password Step ─── */}
          {otpStep === 'email' && (
            <form onSubmit={handleSubmitOtpEmail(onSubmitEmail)}>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  type="email"
                  placeholder={t('login.email')}
                  error={!!errorsOtpEmail.email}
                  helperText={errorsOtpEmail.email?.message}
                  sx={inputSx}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                  {...registerOtpEmail('email', {
                    required: t('login.validation.emailRequired'),
                    pattern: { value: EMAIL_PATTERN, message: t('login.validation.emailInvalid') },
                  })}
                />

                <TextField
                  fullWidth
                  type={showPassword ? 'text' : 'password'}
                  placeholder={t('login.password')}
                  error={!!errorsOtpEmail.password}
                  helperText={errorsOtpEmail.password?.message}
                  sx={inputSx}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                      endAdornment: passwordValue ? (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                            tabIndex={-1}
                            sx={{ color: 'rgba(255,255,255,0.4)' }}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    },
                  }}
                  {...registerOtpEmail('password', {
                    required: t('login.validation.passwordRequired'),
                    minLength: { value: 1, message: t('login.validation.passwordMinLength') },
                  })}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={passwordLoginMutation.isPending}
                  sx={{
                    mt: 1,
                    py: 1.5,
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    bgcolor: '#00B8D4',
                    color: '#000',
                    '&:hover': { bgcolor: '#0097b2', transform: 'translateY(-1px)' },
                    '&:disabled': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.3)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  {passwordLoginMutation.isPending ? t('login.loggingIn') : t('login.loginButton')}
                </Button>

                <Box sx={{ textAlign: 'right', mt: -0.5 }}>
                  <MuiLink
                    href="/forgot-password"
                    sx={{
                      color: 'rgba(255,255,255,0.5)',
                      textDecoration: 'none',
                      fontSize: '0.8rem',
                      '&:hover': { color: '#00B8D4' },
                    }}
                  >
                    {t('login.forgotPassword')}
                  </MuiLink>
                </Box>

                <Divider
                  sx={{
                    my: 1,
                    '&::before, &::after': { borderColor: 'rgba(255,255,255,0.1)' },
                    '& .MuiDivider-wrapper': { color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem' },
                  }}
                >
                  {t('login.orDivider')}
                </Divider>

                {/* OAuth */}
                {inWebView ? (
                  <Alert severity="info" sx={{ textAlign: 'left' }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t('login.webViewHint', {
                        app: webViewAppName || t('login.webViewDefaultApp'),
                      })}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1.5 }}>
                      {t('login.webViewCopyHint')}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={urlCopied ? <CheckIcon /> : <ContentCopyIcon />}
                      onClick={handleCopyUrl}
                      sx={{
                        textTransform: 'none',
                        color: urlCopied ? 'success.main' : 'primary.main',
                        borderColor: urlCopied ? 'success.main' : 'primary.main',
                      }}
                    >
                      {urlCopied ? t('login.urlCopiedButton') : t('login.copyUrlButton')}
                    </Button>
                  </Alert>
                ) : (
                  <Button
                    fullWidth
                    variant="outlined"
                    size="large"
                    onClick={handleGoogleLogin}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 500,
                      borderRadius: '10px',
                      borderColor: 'rgba(255,255,255,0.2)',
                      bgcolor: 'rgba(255,255,255,0.06)',
                      color: 'rgba(255,255,255,0.8)',
                      py: 1.3,
                      '&:hover': {
                        borderColor: 'rgba(255,255,255,0.35)',
                        bgcolor: 'rgba(255,255,255,0.1)',
                      },
                    }}
                  >
                    <Box
                      component="img"
                      src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cGF0aCBkPSJNMTguMiAxMC4yYzAtLjctLjEtMS4zLS4yLTEuOUgxMHYzLjdoNC42Yy0uMiAxLS44IDEuOC0xLjYgMi40djJoMi42YzEuNS0xLjQgMi40LTMuNSAyLjQtNi4xeiIgZmlsbD0iIzQyODVGNCIvPgogIDxwYXRoIGQ9Ik0xMCAxOC41YzIuMiAwIDQtLjcgNS40LTJsLTIuNi0yYy0uNy41LTEuNy44LTIuOC44LTIuMSAwLTMuOS0xLjQtNC42LTMuNEgyLjZ2Mi4xQzQgMTYuNCA2LjggMTguNSAxMCAxOC41eiIgZmlsbD0iIzM0QTg1MyIvPgogIDxwYXRoIGQ9Ik01LjQgMTEuOWMtLjItLjUtLjMtMS4xLS4zLTEuN3MuMS0xLjIuMy0xLjdWNi40SDIuNkMyIDcuNiAxLjYgOC44IDEuNiAxMHMuNCAyLjQgMSAzLjZsMi44LTIuN3oiIGZpbGw9IiNGQkJDMDUiLz4KICA8cGF0aCBkPSJNMTAgNC43YzEuMiAwIDIuMy40IDMuMSAxLjJsMi4zLTIuM0MxMy45IDIuMiAxMi4xIDEuNSAxMCAxLjUgNi44IDEuNSA0IDMuNiAyLjYgNi40bDIuOCAyLjFjLjctMiAyLjUtMy40IDQuNi0zLjR6IiBmaWxsPSIjRUE0MzM1Ii8+Cjwvc3ZnPg=="
                      alt="Google"
                      sx={{ mr: 1, height: 20 }}
                    />
                    {t('login.googleLogin')}
                  </Button>
                )}

                <Typography
                  sx={{
                    textAlign: 'center',
                    mt: 2,
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '0.85rem',
                  }}
                >
                  {t('login.noAccount')}{' '}
                  <MuiLink
                    href="/register"
                    sx={{
                      color: '#00B8D4',
                      textDecoration: 'none',
                      fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {t('login.registerNow')}
                  </MuiLink>
                </Typography>
              </Stack>
            </form>
          )}

          {/* ── OTP Verification Step ─── */}
          {otpStep === 'verification' && (
            <form onSubmit={handleSubmitOtpCode(onSubmitOtp)}>
              <Stack spacing={2}>
                {verificationTimeRemaining > 0 && (
                  <Typography
                    sx={{
                      textAlign: 'right',
                      color: verificationTimeRemaining <= 30 ? '#ef4444' : 'rgba(255,255,255,0.4)',
                      fontWeight: verificationTimeRemaining <= 30 ? 700 : 400,
                      fontSize: '0.85rem',
                    }}
                  >
                    {Math.floor(verificationTimeRemaining / 60)}:
                    {String(verificationTimeRemaining % 60).padStart(2, '0')}
                  </Typography>
                )}

                <TextField
                  fullWidth
                  placeholder={t('login.verification.enterCode', { length: OTP_CODE_LENGTH })}
                  type="text"
                  inputProps={{ maxLength: OTP_CODE_LENGTH, inputMode: 'numeric' }}
                  error={!!errorsOtpCode.otp}
                  helperText={errorsOtpCode.otp?.message}
                  sx={inputSx}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                  {...registerOtpCode('otp', {
                    required: t('login.verification.codeRequired'),
                    pattern: {
                      value: OTP_CODE_PATTERN,
                      message: t('login.verification.codeFormat', { length: OTP_CODE_LENGTH }),
                    },
                  })}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={verifyOtpMutation.isPending}
                  sx={{
                    py: 1.5,
                    borderRadius: '10px',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    bgcolor: '#00B8D4',
                    color: '#000',
                    '&:hover': { bgcolor: '#0097b2' },
                    '&:disabled': {
                      bgcolor: 'rgba(255,255,255,0.1)',
                      color: 'rgba(255,255,255,0.3)',
                    },
                  }}
                >
                  {verifyOtpMutation.isPending
                    ? t('login.verification.verifying')
                    : t('login.verification.verify')}
                </Button>

                <Box sx={{ textAlign: 'center' }}>
                  {otpResendCountdown > 0 ? (
                    <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>
                      {t('login.verification.resendIn', { seconds: otpResendCountdown })}
                    </Typography>
                  ) : (
                    <Button
                      variant="text"
                      size="small"
                      onClick={handleResendOtp}
                      disabled={sendOtpMutation.isPending}
                      sx={{ textTransform: 'none', color: '#00B8D4' }}
                    >
                      {sendOtpMutation.isPending
                        ? t('login.verification.resending')
                        : t('login.verification.resend')}
                    </Button>
                  )}
                </Box>

                <Button
                  variant="text"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBackToEmail}
                  sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)' }}
                >
                  {t('login.verification.back')}
                </Button>
              </Stack>
            </form>
          )}
        </Box>
      </Box>

      {/* ── Right: Visual Panel (hidden on mobile) ── */}
      <Box
        sx={{
          display: { xs: 'none', lg: 'flex' },
          flex: 1,
          alignItems: 'flex-start',
          justifyContent: 'center',
          pt: '40vh',
          position: 'relative',
          m: 2,
          borderRadius: 4,
          overflow: 'hidden',
          background: '#0a0a0a',
        }}
      >
        {/* Particle animation background */}
        <ParticleBackground />

        {/* Tagline overlay */}
        <Box sx={{ position: 'relative', textAlign: 'center', px: 6, zIndex: 1 }}>
          <Typography
            sx={{
              fontFamily:
                'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              fontWeight: 600,
              fontSize: { lg: '2.6rem', xl: '3rem' },
              color: '#fff',
              lineHeight: 1.15,
              mb: 2,
              letterSpacing: '-0.02em',
            }}
          >
            {t('login.tagline1')}
            <Box component="span" sx={{ color: '#00B8D4', ml: 2.5 }}>
              {t('login.tagline2')}
            </Box>
          </Typography>
          <Typography
            sx={{
              fontFamily:
                'ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '1rem',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.5,
            }}
          >
            {t('login.taglineDescription')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
